import { Link, useLocation } from "react-router-dom";
import { Instagram, Mail } from "lucide-react";

// TikTok icon component
const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export default function Footer() {
  const location = useLocation();
  
  // Hide footer on admin page
  if (location.pathname === "/admin") return null;

  const currentYear = new Date().getFullYear();

  return (
    <footer className="border-t border-zinc-200" data-testid="footer">
      {/* Massive Brand Text */}
      <div className="overflow-hidden py-8 md:py-12">
        <div className="text-center select-none font-serif text-[12vw] md:text-[14vw] font-bold leading-none text-zinc-100 tracking-tight">
          FILMCABLE
        </div>
      </div>

      {/* Footer Content */}
      <div className="border-t border-zinc-200 py-12 md:py-16">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* About */}
            <div className="md:col-span-2">
              <h3 className="font-serif text-xl font-bold mb-4">Filmcable</h3>
              <p className="text-zinc-600 leading-relaxed mb-6 max-w-md">
                A film media publisher delivering authoritative coverage of cinema, 
                film festivals, and the global film industry.
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="https://instagram.com/filmcable"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon"
                  data-testid="footer-instagram-link"
                  aria-label="Instagram"
                >
                  <Instagram size={18} />
                </a>
                <a
                  href="https://tiktok.com/@filmcable_"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon"
                  data-testid="footer-tiktok-link"
                  aria-label="TikTok"
                >
                  <TikTokIcon size={18} />
                </a>
                <a
                  href="mailto:press@filmcable.net"
                  className="social-icon"
                  data-testid="footer-email-link"
                  aria-label="Email"
                >
                  <Mail size={18} />
                </a>
              </div>
            </div>

            {/* Navigation */}
            <div>
              <h4 className="font-accent text-sm font-bold uppercase tracking-widest text-zinc-400 mb-4">
                Navigate
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/"
                    className="text-zinc-600 hover:text-[#FF66C4] transition-colors"
                    data-testid="footer-home-link"
                  >
                    Home
                  </Link>
                </li>
                <li>
                  <Link
                    to="/articles"
                    className="text-zinc-600 hover:text-[#FF66C4] transition-colors"
                    data-testid="footer-editorial-link"
                  >
                    Editorial
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    className="text-zinc-600 hover:text-[#FF66C4] transition-colors"
                    data-testid="footer-about-link"
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    to="/press"
                    className="text-zinc-600 hover:text-[#FF66C4] transition-colors"
                    data-testid="footer-press-link"
                  >
                    Press
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-accent text-sm font-bold uppercase tracking-widest text-zinc-400 mb-4">
                Contact
              </h4>
              <ul className="space-y-3">
                <li>
                  <p className="text-zinc-600">Press Inquiries</p>
                  <a
                    href="mailto:press@filmcable.net"
                    className="text-[#FF66C4] hover:underline font-medium"
                    data-testid="footer-press-email-link"
                  >
                    press@filmcable.net
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-zinc-200 py-6">
        <div className="container flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-zinc-500">
            © {currentYear} Filmcable. All rights reserved.
          </p>
          <p className="text-sm text-zinc-500">
            A Film Media Publisher
          </p>
        </div>
      </div>
    </footer>
  );
}
